import { SetStateAction, useContext, useState } from "react";
import { DeviceDto } from "../../dtos/DeviceDto";
import { Asset } from "../../data/Asset";
import { LoadingButton } from "@mui/lab";
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, TextField, Card, CardContent, CardHeader, IconButton, Stack, Tooltip, CardActions } from "@mui/material";
import VspPortalContext from "../../store/VspPortalContext";
import { getDefaultWifiKeyExpiry } from "../../utils";
import { WifiKeyDto } from "../../dtos/WifiKeyDto";
import RefreshIcon from '@mui/icons-material/Refresh';
import { SelectVillage } from "../../components/SelectVillage";
import { Dashboard } from "../../enums/Dashboard";
import PublishIcon from '@mui/icons-material/Publish';
import { useVillageWifiHttp } from "./hooks/useVillageWifiHttp";
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import { WifiKeyExpiry } from "./WifiKeyExpiry";
import TabletAndroidIcon from '@mui/icons-material/TabletAndroid';


type EnterDeviceDetailsProps = {

    onSetWifiKey( wifiKeyDto :  WifiKeyDto | undefined ) : void;

};


const textFieldSx = {

    marginTop: '10px', 

};


export const EnterDeviceDetails : React.FC< EnterDeviceDetailsProps > = ( props: EnterDeviceDetailsProps ) => {

    const { onSetWifiKey } = props;

    const [ asset, setAsset ] = useState< Asset >( Asset.Waio );
    const [ canSubmit, setCanSubmit ] = useState< boolean >( false );
    const [ description, setDescription ] = useState<string>( '' );
    const [ descriptionLabel, setDescriptionLabel ] = useState<string>( 'Device Name *' );
    const [ expiry, setExpiry ] = useState< string | undefined  >( getDefaultWifiKeyExpiry( ) );
    const [ isDisabled, setIsDisabled ] = useState< boolean >( false );
    const [ isSubmitting, setIsSubmitting ] = useState< boolean >( false );
    const [ resetWifiKeyExpiry, setResetWifiKeyExpiry ] = useState< number >( 0 );
    const [ village, setVillage ] = useState< string >( '' );


    const { state } = useContext( VspPortalContext );


    const { getDeviceWifiKey } = useVillageWifiHttp( );


    const generateDeviceWifiKey = async( ) => {

        setCanSubmit( false );
        setIsDisabled( true );
        
        const device: DeviceDto = {
            
            createdBy: state.username
            , comments: description.trim( )
            , keyExpiryDate: expiry
            , villageCode: village
        
        };

        setIsSubmitting( true );

        const wifiKeyDto = await getDeviceWifiKey( device );

        setIsSubmitting( false );

        if( wifiKeyDto ) {

            onSetWifiKey( wifiKeyDto );

        }

    };


    const handleGenerateWifiPasswordClick = ( ) => {

        if( !canSubmit ) {
            
            return;

        }

        generateDeviceWifiKey( );

    }


    const handleResetClick = ( ) => {

        setAsset( Asset.Waio );
        setDescription( '' );
        setIsDisabled( false );
        onSetWifiKey( undefined );
        setCanSubmit( false );
        setVillage( '' );
        setResetWifiKeyExpiry( v => v + 1 );

    };


    const handleSelectVillage = ( villageCode : string ) => {

        setVillage( villageCode );
        setCanSubmit( villageCode &&  description.length > 2 ? true : false );

    };
   
    
    const handleAssetChange = ( event: React.ChangeEvent< HTMLInputElement > ) => {

        const asset = ( event.target as HTMLInputElement ).value as Asset
        setAsset( asset );

    };


    const handleDescriptionChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        setDescription( event.target.value.trimStart( ) );
        setCanSubmit( village && event.target.value.trim( ).length > 2 ? true : false );
        setDescriptionLabel( event.target.value.trim( ).length > 0 ? 'Device Name' : 'Device Name *' );

    };


    const handleDescriptionKeyPress = async ( event: React.KeyboardEvent<HTMLDivElement> ) => {

        if( !canSubmit || event.key !== 'Enter' ) {
            
            return;

        }

        generateDeviceWifiKey( );

    };


    const handleSelectWifiKeyExpiry = ( expiry : SetStateAction< string | undefined > ) => setExpiry( expiry );


    return(

        <Card sx={{ boxShadow: 5, maxWidth: 'fit-content' }}>

            <CardHeader 
                sx={{ fontWeight: '600', fontSize: '20px', paddingTop: '5px', paddingBottom: '0' }}
                title="Device Details"
                avatar={
                    <TabletAndroidIcon />
                }
                action={
                    <Tooltip title="Reset">
                        <IconButton onClick={ handleResetClick }>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                }
            />

            <CardContent>
                
                <Stack gap={ 0.5 }>
                    
                    <FormControl 
                        sx={{marginTop: '10px'}}
                        disabled={ isDisabled }
                    >
                        <FormLabel>Asset</FormLabel>
                        <RadioGroup 
                            row
                            value={asset}
                            onChange={ handleAssetChange }
                        >
                            <FormControlLabel value={ Asset.Waio } control={<Radio />} label={ Asset.Waio } />
                            <FormControlLabel value={ Asset.Niw } control={<Radio />} label={ Asset.Niw } />
                        </RadioGroup>
                    </FormControl>
                    
                    <SelectVillage asset={ asset } dashboard={ Dashboard.VillageWiFi } onSelectVillage={ handleSelectVillage } village={ village }/>

                    <TextField 
                        sx={{ ...textFieldSx, marginBottom: '10px' }} 
                        label={ descriptionLabel }
                        variant="outlined"
                        value={ description }
                        onChange={ handleDescriptionChange }
                        onKeyPress={ handleDescriptionKeyPress }
                        disabled={ isDisabled }
                    />
              
                    <WifiKeyExpiry expiryOptions={ [ '1', '7', 'N', 'C' ]} isDisabled={ isDisabled } resetWifiKeyExpiry={ resetWifiKeyExpiry } selectWifiKeyExpiry={ handleSelectWifiKeyExpiry } />

                </Stack>

            </CardContent>

            <CardActions
                sx={{
                    display: "flex",
                    justifyContent: "flex-end"
                }}
            >

                <LoadingButton
                    sx={{ fontWeight: 'bold', marginBottom: '10px', marginRight: '10px', textTransform: 'none', height: '47px' }}
                    loadingPosition="start"
                    startIcon={<PublishIcon />}
                    variant="contained"
                    disabled={ !canSubmit }
                    onClick={ handleGenerateWifiPasswordClick }
                    loading={ isSubmitting }
                >
                    Generate Wi-Fi password
                </LoadingButton>
                
            </CardActions>

        </Card>

    );

};